/**
 * Settings → Betriebsparameter (HOF-708) — one editor per farm setting, keyed `namespace.key`.
 *
 * Each editor gets its item from `GET /api/v1/farm/settings` (value, version, `writable`) and the
 * registry's vocabularies, keeps a draft, and saves the WHOLE value with the version it read; a stale
 * version is a 409 the editor names, a value the registry refuses a 422 whose message it shows. An item
 * the seat may only read renders its controls disabled and offers no save.
 */
import { useEffect, useId, useMemo, useState, type ComponentType, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2 } from 'lucide-react';
import { Button, Input, NativeSelect, SettingsRow, SettingsSection } from '@hofsync/design-system';
import api from '../../services/api';
import { sourcesService } from '../../services/sourcesService';
import { useToast } from '../../contexts/ToastContext';
import {
    CATALOGUE_MAX_ACTIVITIES,
    CATALOGUE_MAX_COST_OBJECTS,
    GROUP_LABELS,
    MATCH_KEY_LABELS,
    REGISTER_LABELS,
    SYSTEM_LABELS,
    catalogueProblem,
    checkShareRows,
    formatPercent,
    keyFromLabel,
    labelOf,
    rowsFromValue,
    settingId,
    settingUrl,
    type CatalogueActivity,
    type CatalogueCostObject,
    type DeclaredSource,
    type EditorOptions,
    type FarmSettingItem,
    type FixedShares,
    type LabourAccess,
    type LabourCatalogue,
    type ShareRow,
} from './farmParameters';

export const SETTINGS_LIST_KEY = ['farm', 'settings'] as const;
const opts = { withCredentials: true };

export interface EditorProps {
    item: FarmSettingItem;
    options: EditorOptions;
}

type ApiError = { response?: { status?: number; data?: { detail?: unknown } } };

/** Save one setting with the version it was read at; refresh the list on success. */
function useSaveSetting(item: FarmSettingItem, savedMessage?: string) {
    const { t } = useTranslation();
    const { showToast } = useToast();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (value: unknown) =>
            (await api.put(settingUrl(settingId(item)), { value, expected_version: item.version }, opts)).data,
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: SETTINGS_LIST_KEY });
            showToast(savedMessage ?? t('settings.parameters.saved', 'Gespeichert'), 'success');
        },
        onError: (error: unknown) => {
            const response = (error as ApiError)?.response;
            const detail = response?.data?.detail as { error_kind?: string; message?: string } | undefined;
            let message = t('settings.parameters.saveFailed', 'Die Änderung konnte nicht gespeichert werden.');
            if (detail?.error_kind === 'setting_busy') {
                message = t(
                    'settings.parameters.busy',
                    'Gerade ändert jemand anderes die Datenquellen. Bitte versuche es gleich noch einmal.'
                );
            } else if (detail?.error_kind === 'master_replacement_not_built') {
                message = t(
                    'settings.parameters.masterReplacement',
                    'Die führende Quelle eines Bereichs lässt sich nicht wechseln, solange es dazu schon Daten gibt.'
                );
            } else if (detail?.error_kind === 'translation_unavailable') {
                message = t(
                    'settings.parameters.catalogue.translationUnavailable',
                    'Die Namen konnten gerade nicht übersetzt werden. Bitte versuche es gleich noch einmal.'
                );
            } else if (response?.status === 409) {
                message = t(
                    'settings.parameters.conflict',
                    'Jemand hat diese Einstellung inzwischen geändert. Bitte lade die Seite neu.'
                );
            } else if (response?.status === 422 && detail?.message) {
                message = `${t('settings.parameters.invalid', 'Ungültig')}: ${detail.message}`;
            }
            showToast(message, 'error');
        },
    });
}

/** A draft of the item's value, reset whenever the stored value changes. */
function useDraft<T>(value: T) {
    const [draft, setDraft] = useState<T>(value);
    const stored = JSON.stringify(value);
    useEffect(() => {
        setDraft(JSON.parse(stored) as T);
    }, [stored]);
    return { draft, setDraft, dirty: JSON.stringify(draft) !== stored };
}

interface SectionProps {
    item: FarmSettingItem;
    title: string;
    description: ReactNode;
    dirty: boolean;
    valid: boolean;
    saving: boolean;
    onSave: () => void;
    saveLabel?: string;
    children: ReactNode;
}

function EditorSection({ item, title, description, dirty, valid, saving, onSave, saveLabel, children }: SectionProps) {
    const { t } = useTranslation();
    return (
        <SettingsSection
            title={title}
            description={description}
            action={
                item.writable ? (
                    <Button size="sm" onClick={onSave} disabled={!dirty || !valid || saving}>
                        {saveLabel ?? t('settings.parameters.save', 'Speichern')}
                    </Button>
                ) : undefined
            }
        >
            {children}
            {!item.writable && (
                <p className="farm-parameters__hint">
                    {t('settings.parameters.readOnlyGeneric', 'Diese Einstellung kannst du ansehen, aber nicht ändern.')}
                </p>
            )}
        </SettingsSection>
    );
}

function Checkbox({
    label,
    checked,
    disabled,
    onChange,
}: {
    label: string;
    checked: boolean;
    disabled?: boolean;
    onChange: (checked: boolean) => void;
}) {
    return (
        <label className="farm-parameters__check">
            <input type="checkbox" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
            <span>{label}</span>
        </label>
    );
}

/* ----------------------------------------------------------------------- Fester Kulturanteil */

function FixedSharesEditor({ item }: EditorProps) {
    const { t } = useTranslation();
    const cropListId = useId();
    const value = item.value as FixedShares;
    const crops = useQuery({
        queryKey: ['twin', 'sources', 'terms', 'crop'],
        queryFn: () => sourcesService.getCropTerms(),
        enabled: item.writable,
        staleTime: 5 * 60_000,
    });
    const [rows, setRows] = useState<ShareRow[]>(() => rowsFromValue(value));
    const loaded = JSON.stringify(rowsFromValue(value));
    useEffect(() => {
        setRows(JSON.parse(loaded) as ShareRow[]);
    }, [loaded]);
    const check = useMemo(() => checkShareRows(rows), [rows]);
    const dirty = JSON.stringify(rows) !== loaded;
    const save = useSaveSetting(item, t('settings.parameters.fixedShares.saved', 'Anteile gespeichert'));
    const update = (index: number, patch: Partial<ShareRow>) =>
        setRows((current) => current.map((row, i) => (i === index ? { ...row, ...patch } : row)));

    const title = t('settings.parameters.fixedShares.title', 'Fester Kulturanteil');
    const description = (
        <>
            {t(
                'settings.parameters.fixedShares.description',
                'So verteilt sich der Arbeitgeberaufwand der festangestellten Mitarbeiter ohne Stundenerfassung auf die Kulturen: alle Mitarbeiter der Kostenstelle 01 in der Lohnabrechnung, die im Monat keine Stunden erfasst haben.'
            )}{' '}
            {t(
                'settings.parameters.fixedShares.smallMonth',
                'Sind es in einem Monat weniger als drei, wird ihr Aufwand aus Datenschutzgründen nicht nach Kulturen getrennt, sondern über die erfassten Arbeitsstunden verteilt.'
            )}{' '}
            {t(
                'settings.parameters.fixedShares.effect',
                'Eine Änderung wirkt, sobald die Lohnabrechnung (LODAS) des Monats das nächste Mal eingelesen wird.'
            )}
        </>
    );
    const problems: Record<NonNullable<typeof check.problem>, string> = {
        cropMissing: t('settings.parameters.fixedShares.cropMissing', 'Bitte gib für jede Zeile eine Kultur an.'),
        cropTooLong: t('settings.parameters.fixedShares.cropTooLong', 'Ein Kulturname ist zu lang.'),
        cropTwice: t('settings.parameters.fixedShares.cropTwice', 'Jede Kultur darf nur einmal vorkommen.'),
        shareRange: t('settings.parameters.fixedShares.shareRange', 'Jeder Anteil muss über 0 und höchstens 100 % sein.'),
        sumNot100: t('settings.parameters.fixedShares.sumNot100', 'Die Anteile müssen zusammen genau 100 % ergeben.'),
        tooMany: t('settings.parameters.fixedShares.tooMany', 'Höchstens 20 Kulturen.'),
    };

    if (!item.writable) {
        return (
            <SettingsSection title={title} description={description}>
                {value.shares.length === 0 ? (
                    <p>{t('settings.parameters.fixedShares.empty', 'Kein fester Kulturanteil hinterlegt.')}</p>
                ) : (
                    <ul className="farm-parameters__readonly" aria-label={title}>
                        {value.shares.map((s) => (
                            <li key={s.crop}>
                                <span>{s.crop}</span>
                                <span>{formatPercent(s.share)} %</span>
                            </li>
                        ))}
                    </ul>
                )}
                <p className="farm-parameters__hint">
                    {t('settings.parameters.readOnly', 'Ändern können Inhaber, Betriebsleitung und Buchhaltung.')}
                </p>
            </SettingsSection>
        );
    }

    return (
        <EditorSection
            item={item}
            title={title}
            description={description}
            dirty={dirty}
            valid={check.value !== null}
            saving={save.isPending}
            onSave={() => check.value && save.mutate(check.value)}
            saveLabel={t('settings.parameters.fixedShares.save', 'Anteile speichern')}
        >
            <div className="farm-parameters__rows">
                {rows.length === 0 && <p>{t('settings.parameters.fixedShares.empty', 'Kein fester Kulturanteil hinterlegt.')}</p>}
                {rows.map((row, index) => (
                    <div className="farm-parameters__share" key={index}>
                        <Input
                            aria-label={`${t('settings.parameters.fixedShares.crop', 'Kultur')} ${index + 1}`}
                            placeholder={t('settings.parameters.fixedShares.crop', 'Kultur')}
                            list={cropListId}
                            value={row.crop}
                            maxLength={100}
                            onChange={(e) => update(index, { crop: e.target.value })}
                        />
                        <div className="farm-parameters__percent">
                            <Input
                                aria-label={`${t('settings.parameters.fixedShares.share', 'Anteil in %')} ${index + 1}`}
                                inputMode="decimal"
                                value={row.percent}
                                onChange={(e) => update(index, { percent: e.target.value })}
                            />
                            <span aria-hidden="true">%</span>
                        </div>
                        <Button
                            size="sm"
                            variant="outline"
                            aria-label={`${t('settings.parameters.fixedShares.remove', 'Entfernen')} ${row.crop || index + 1}`}
                            onClick={() => setRows((current) => current.filter((_, i) => i !== index))}
                        >
                            <Trash2 aria-hidden="true" size={14} />
                        </Button>
                    </div>
                ))}
                <datalist id={cropListId}>
                    {(crops.data ?? [])
                        .filter((term) => term.cycles > 0)
                        .map((term) => (
                            <option key={term.term} value={term.term} />
                        ))}
                </datalist>
                <div className="farm-parameters__footer">
                    <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setRows((current) => [...current, { crop: '', percent: '' }])}
                        disabled={rows.length >= 20}
                    >
                        <Plus aria-hidden="true" size={14} /> {t('settings.parameters.fixedShares.add', 'Kultur hinzufügen')}
                    </Button>
                    <span className="farm-parameters__sum" aria-live="polite">
                        {t('settings.parameters.fixedShares.sum', 'Summe')}: {formatPercent(check.sumPercent / 100)} %
                    </span>
                </div>
                {check.problem && (
                    <p className="farm-parameters__problem" role="alert">
                        {problems[check.problem]}
                    </p>
                )}
            </div>
        </EditorSection>
    );
}

/* ------------------------------------------------------------------------- the labour catalogue */

/** German names for the classes `editor_options.activity_classes` serves (the worker's Deckungsbeitrag lines). */
const ACTIVITY_CLASS_LABELS: Record<string, [string, string]> = {
    picking: ['settings.parameters.catalogue.classes.picking', 'Ernte'],
    field_care: ['settings.parameters.catalogue.classes.field_care', 'Pflege'],
    planting: ['settings.parameters.catalogue.classes.planting', 'Pflanzung'],
    packing: ['settings.parameters.catalogue.classes.packing', 'Packen & Sortieren'],
    overhead: ['settings.parameters.catalogue.classes.overhead', 'Gemeinkosten'],
};

const COST_OBJECT_REF_LABELS: Record<string, [string, string]> = {
    crop_cycle_id: ['settings.parameters.catalogue.refs.crop_cycle_id', 'Anbauzyklus'],
    field_id: ['settings.parameters.catalogue.refs.field_id', 'Schlag'],
    cost_center_id: ['settings.parameters.catalogue.refs.cost_center_id', 'Buchhaltungs-Kostenstelle'],
};

/**
 * Settings → Betriebsparameter → Tätigkeiten und Kostenstellen. The farm names an entry in German only;
 * the save translates every blank Romanian/Polish name (`backend/src/services/catalogue_translation.py`),
 * so renaming an entry clears its two translations for the save to redo. A new entry's key follows its
 * German name until it is saved and never changes after — neither is shown, since recorded time refers
 * to the key and the farm only ever sees the name.
 */
function CatalogueEditor({ item, options }: EditorProps) {
    const { t } = useTranslation();
    const stored = item.value as LabourCatalogue;
    const { draft, setDraft, dirty } = useDraft<LabourCatalogue>(stored);
    const save = useSaveSetting(item);
    const savedKeys = useMemo(
        () => ({
            activities: new Set(stored.activities.map((a) => a.key)),
            cost_objects: new Set(stored.cost_objects.map((c) => c.key)),
        }),
        [stored]
    );
    const problem = catalogueProblem(draft);
    const disabled = !item.writable;
    const activity = t('settings.parameters.catalogue.activity', 'Tätigkeit');
    const costObject = t('settings.parameters.catalogue.costObject', 'Kostenstelle');
    const nameTitle = t('settings.parameters.catalogue.nameDe', 'Name (Deutsch)');
    const nameLabel = t('settings.parameters.catalogue.name', 'Name');
    const classTitle = t('settings.parameters.catalogue.activityClass', 'Tätigkeitsart');
    const kindTitle = t('settings.parameters.catalogue.kind', 'Art');
    const linkTitle = t('settings.parameters.catalogue.link', 'Verknüpfung');
    const linkIdTitle = t('settings.parameters.catalogue.linkId', 'ID');
    const removeLabel = t('settings.parameters.remove', 'Entfernen');

    function updateActivity(index: number, patch: Partial<CatalogueActivity>) {
        setDraft((d) => ({ ...d, activities: d.activities.map((a, i) => (i === index ? withKey(a, patch, i, d.activities, savedKeys.activities) : a)) }));
    }
    function updateCostObject(index: number, patch: Partial<CatalogueCostObject>) {
        setDraft((d) => ({ ...d, cost_objects: d.cost_objects.map((c, i) => (i === index ? withKey(c, patch, i, d.cost_objects, savedKeys.cost_objects) : c)) }));
    }

    const problems = {
        labels: t('settings.parameters.catalogue.labels', 'Jeder Eintrag braucht einen deutschen Namen.'),
        keys: t('settings.parameters.catalogue.keys', 'Zwei Einträge haben denselben Namen.'),
        linkId: t('settings.parameters.catalogue.linkIdMissing', 'Eine Verknüpfung braucht eine ID.'),
        tooMany: t('settings.parameters.catalogue.tooMany', 'Höchstens 12 Tätigkeiten und 30 Kostenstellen.'),
    };

    const removeButton = (label: string, onClick: () => void) =>
        disabled ? null : (
            <Button size="icon" variant="ghost" className="farm-parameters__remove" aria-label={`${removeLabel} ${label}`} title={removeLabel} onClick={onClick}>
                <Trash2 aria-hidden="true" size={15} />
            </Button>
        );

    return (
        <EditorSection
            item={item}
            title={t('settings.parameters.catalogue.title', 'Tätigkeiten und Kostenstellen')}
            description={t(
                'settings.parameters.catalogue.description',
                'Was Mitarbeiter in der Arbeitszeit-App erfassen können. Neue Einträge stehen sofort zur Auswahl. Rumänisch und Polnisch werden beim Speichern übersetzt; ein umbenannter Eintrag behält seine erfassten Zeiten.'
            )}
            dirty={dirty}
            valid={problem === null}
            saving={save.isPending}
            onSave={() => save.mutate(draft)}
        >
            <div className="farm-parameters__rows">
                <h3 className="farm-parameters__subtitle">{t('settings.parameters.catalogue.activities', 'Tätigkeiten')}</h3>
                <div className="farm-parameters__table farm-parameters__table--activities">
                    {draft.activities.length > 0 && (
                        <div className="farm-parameters__head" aria-hidden="true">
                            <span>{nameTitle}</span>
                            <span>{classTitle}</span>
                        </div>
                    )}
                    {draft.activities.map((row, index) => {
                        const known = !row.activity_class || options.activity_classes.includes(row.activity_class);
                        return (
                            <div className="farm-parameters__line" key={index}>
                                <Input
                                    aria-label={`${nameLabel} ${activity} ${index + 1}`}
                                    placeholder={nameTitle}
                                    value={row.label_de}
                                    disabled={disabled}
                                    onChange={(e) => updateActivity(index, renamed(row, e.target.value))}
                                />
                                <NativeSelect
                                    aria-label={`${classTitle} ${index + 1}`}
                                    value={row.activity_class ?? ''}
                                    disabled={disabled}
                                    onChange={(e) => updateActivity(index, { activity_class: e.target.value || null })}
                                >
                                    <option value="">{t('settings.parameters.catalogue.noClass', 'Ohne Zuordnung')}</option>
                                    {options.activity_classes.map((cls) => (
                                        <option key={cls} value={cls}>
                                            {ACTIVITY_CLASS_LABELS[cls] ? t(...ACTIVITY_CLASS_LABELS[cls]) : cls}
                                        </option>
                                    ))}
                                    {!known && <option value={row.activity_class!}>{row.activity_class}</option>}
                                </NativeSelect>
                                {removeButton(row.label_de || `${activity} ${index + 1}`, () =>
                                    setDraft((d) => ({ ...d, activities: d.activities.filter((_, i) => i !== index) }))
                                )}
                            </div>
                        );
                    })}
                </div>
                {!disabled && (
                    <div>
                        <Button
                            size="sm"
                            variant="outline"
                            disabled={draft.activities.length >= CATALOGUE_MAX_ACTIVITIES}
                            onClick={() =>
                                setDraft((d) => ({
                                    ...d,
                                    activities: [...d.activities, { key: '', label_de: '', label_ro: '', label_pl: '' }],
                                }))
                            }
                        >
                            <Plus aria-hidden="true" size={14} /> {t('settings.parameters.catalogue.addActivity', 'Tätigkeit hinzufügen')}
                        </Button>
                    </div>
                )}

                <h3 className="farm-parameters__subtitle">{t('settings.parameters.catalogue.costObjects', 'Kostenstellen')}</h3>
                <div className="farm-parameters__table farm-parameters__table--cost-objects">
                    {draft.cost_objects.length > 0 && (
                        <div className="farm-parameters__head" aria-hidden="true">
                            <span>{nameTitle}</span>
                            <span>{kindTitle}</span>
                            <span>{linkTitle}</span>
                            <span>{linkIdTitle}</span>
                        </div>
                    )}
                    {draft.cost_objects.map((object, index) => {
                        const ref = linkOf(object, options.cost_object_refs);
                        return (
                            <div className="farm-parameters__line" key={index}>
                                <Input
                                    aria-label={`${nameLabel} ${costObject} ${index + 1}`}
                                    placeholder={nameTitle}
                                    value={object.label_de}
                                    disabled={disabled}
                                    onChange={(e) => updateCostObject(index, renamed(object, e.target.value))}
                                />
                                <NativeSelect
                                    aria-label={`${kindTitle} ${index + 1}`}
                                    value={object.kind}
                                    disabled={disabled}
                                    onChange={(e) => updateCostObject(index, { kind: e.target.value })}
                                >
                                    {options.cost_object_kinds.map((kind) => (
                                        <option key={kind} value={kind}>
                                            {kind === 'crop'
                                                ? t('settings.parameters.catalogue.crop', 'Kultur')
                                                : t('settings.parameters.catalogue.area', 'Bereich')}
                                        </option>
                                    ))}
                                </NativeSelect>
                                <NativeSelect
                                    aria-label={`${linkTitle} ${index + 1}`}
                                    value={ref}
                                    disabled={disabled}
                                    onChange={(e) => {
                                        const id = ref ? (object[ref as keyof CatalogueCostObject] as string) : '';
                                        const cleared = Object.fromEntries(options.cost_object_refs.map((r) => [r, null]));
                                        updateCostObject(index, { ...cleared, ...(e.target.value ? { [e.target.value]: id || '' } : {}) });
                                    }}
                                >
                                    <option value="">{t('settings.parameters.catalogue.noLink', 'Keine Verknüpfung')}</option>
                                    {options.cost_object_refs.map((r) => (
                                        <option key={r} value={r}>
                                            {COST_OBJECT_REF_LABELS[r] ? t(...COST_OBJECT_REF_LABELS[r]) : r}
                                        </option>
                                    ))}
                                </NativeSelect>
                                {ref ? (
                                    <Input
                                        aria-label={`${linkIdTitle} ${index + 1}`}
                                        placeholder={linkIdTitle}
                                        value={(object[ref as keyof CatalogueCostObject] as string) ?? ''}
                                        disabled={disabled}
                                        onChange={(e) => updateCostObject(index, { [ref]: e.target.value })}
                                    />
                                ) : (
                                    <span className="farm-parameters__empty" aria-hidden="true">—</span>
                                )}
                                {removeButton(object.label_de || `${costObject} ${index + 1}`, () =>
                                    setDraft((d) => ({ ...d, cost_objects: d.cost_objects.filter((_, i) => i !== index) }))
                                )}
                            </div>
                        );
                    })}
                </div>
                {!disabled && (
                    <div>
                        <Button
                            size="sm"
                            variant="outline"
                            disabled={draft.cost_objects.length >= CATALOGUE_MAX_COST_OBJECTS}
                            onClick={() =>
                                setDraft((d) => ({
                                    ...d,
                                    cost_objects: [
                                        ...d.cost_objects,
                                        { key: '', kind: options.cost_object_kinds[0] ?? 'crop', label_de: '', label_ro: '', label_pl: '' },
                                    ],
                                }))
                            }
                        >
                            <Plus aria-hidden="true" size={14} /> {t('settings.parameters.catalogue.addCostObject', 'Kostenstelle hinzufügen')}
                        </Button>
                    </div>
                )}
                {problem && dirty && (
                    <p className="farm-parameters__problem" role="alert">
                        {problems[problem]}
                    </p>
                )}
            </div>
        </EditorSection>
    );
}

/** The twin ref a cost object is linked through — chosen as soon as it is set, before its ID is typed. */
function linkOf(object: CatalogueCostObject, refs: string[]): string {
    return refs.find((r) => object[r as keyof CatalogueCostObject] != null) ?? '';
}

/** A new German name: the old Romanian and Polish names no longer match it, so the save translates it anew. */
function renamed<T extends { label_de: string; label_ro: string; label_pl: string }>(row: T, labelDe: string): Partial<T> {
    return (labelDe === row.label_de ? {} : { label_de: labelDe, label_ro: '', label_pl: '' }) as Partial<T>;
}

/** A new row takes its key from its German name until it is saved; a saved key never changes. */
function withKey<T extends { key: string; label_de: string }>(
    row: T,
    patch: Partial<T>,
    index: number,
    rows: T[],
    saved: Set<string>
): T {
    const next = { ...row, ...patch };
    if (saved.has(row.key) || 'key' in patch || patch.label_de === undefined) return next;
    const taken = new Set(rows.filter((_, i) => i !== index).map((r) => r.key));
    return { ...next, key: keyFromLabel(next.label_de, taken) };
}

/* ------------------------------------------------------------------- who may sign in (labour.access) */

function AccessEditor({ item, options }: EditorProps) {
    const { t } = useTranslation();
    const { draft, setDraft, dirty } = useDraft<LabourAccess>(item.value as LabourAccess);
    const save = useSaveSetting(item);
    const channels = draft.channels ?? options.employment_channels;
    const modeLabel = (mode: string) =>
        mode === 'opt_in'
            ? t('settings.parameters.access.optIn', 'Nur Mitarbeiter, die in der Personalkarte freigeschaltet sind')
            : t('settings.parameters.access.employed', 'Alle beschäftigten Mitarbeiter der gewählten Beschäftigungsarten');
    const channelLabel = (channel: string) =>
        channel === 'direct'
            ? t('settings.parameters.access.direct', 'Direkt angestellt')
            : t('settings.parameters.access.agency', 'Über eine Agentur');
    return (
        <EditorSection
            item={item}
            title={t('settings.parameters.access.title', 'Wer sich in der App anmelden darf')}
            description={t(
                'settings.parameters.access.description',
                'Gesperrte Mitarbeiter bleiben immer draußen. Der Abgleich übernimmt eine Änderung innerhalb von etwa 10 Minuten.'
            )}
            dirty={dirty}
            valid={channels.length > 0}
            saving={save.isPending}
            onSave={() => save.mutate(draft)}
        >
            <SettingsRow label={t('settings.parameters.access.mode', 'Zugang')} htmlFor="labour-access-mode" field>
                <NativeSelect
                    id="labour-access-mode"
                    value={draft.mode}
                    disabled={!item.writable}
                    onChange={(e) => setDraft((d) => ({ ...d, mode: e.target.value }))}
                >
                    {options.labour_access_modes.map((mode) => (
                        <option key={mode} value={mode}>
                            {modeLabel(mode)}
                        </option>
                    ))}
                </NativeSelect>
            </SettingsRow>
            <SettingsRow label={t('settings.parameters.access.channels', 'Beschäftigungsarten')}>
                <div className="farm-parameters__checks">
                    {options.employment_channels.map((channel) => (
                        <Checkbox
                            key={channel}
                            label={channelLabel(channel)}
                            checked={channels.includes(channel)}
                            disabled={!item.writable}
                            onChange={(on) =>
                                setDraft((d) => ({
                                    ...d,
                                    channels: options.employment_channels.filter((c) =>
                                        c === channel ? on : (d.channels ?? options.employment_channels).includes(c)
                                    ),
                                }))
                            }
                        />
                    ))}
                </div>
            </SettingsRow>
        </EditorSection>
    );
}

/* ------------------------------------------------------------------------------ the data sources */

function DeclaredEditor({ item, options }: EditorProps) {
    const { t } = useTranslation();
    const { draft, setDraft, dirty } = useDraft<DeclaredSource[]>(item.value as DeclaredSource[]);
    const save = useSaveSetting(item);
    const used = draft.map((d) => d.system);
    const valid = draft.every((d) => d.registers.length > 0) && new Set(used).size === used.length;
    return (
        <EditorSection
            item={item}
            title={t('settings.parameters.declared.title', 'Genutzte Systeme')}
            description={t('settings.parameters.declared.description', 'Welche Systeme dein Betrieb nutzt, und wofür.')}
            dirty={dirty}
            valid={valid}
            saving={save.isPending}
            onSave={() => save.mutate(draft)}
        >
            <div className="farm-parameters__rows">
                {draft.length === 0 && <p>{t('settings.parameters.declared.empty', 'Keine Systeme hinterlegt.')}</p>}
                {draft.map((entry, index) => (
                    <div className="farm-parameters__entry" key={index}>
                        <NativeSelect
                            aria-label={`${t('settings.parameters.system', 'System')} ${index + 1}`}
                            value={entry.system}
                            disabled={!item.writable}
                            onChange={(e) =>
                                setDraft((d) => d.map((x, i) => (i === index ? { ...x, system: e.target.value } : x)))
                            }
                        >
                            {options.systems.map((system) => (
                                <option key={system} value={system}>
                                    {labelOf(SYSTEM_LABELS, system)}
                                </option>
                            ))}
                        </NativeSelect>
                        <div className="farm-parameters__checks">
                            {options.registers.map((register) => (
                                <Checkbox
                                    key={register}
                                    label={labelOf(REGISTER_LABELS, register)}
                                    checked={entry.registers.includes(register)}
                                    disabled={!item.writable}
                                    onChange={(on) =>
                                        setDraft((d) =>
                                            d.map((x, i) =>
                                                i === index
                                                    ? {
                                                          ...x,
                                                          registers: options.registers.filter((r) =>
                                                              r === register ? on : x.registers.includes(r)
                                                          ),
                                                      }
                                                    : x
                                            )
                                        )
                                    }
                                />
                            ))}
                        </div>
                        {item.writable && (
                            <Button
                                size="sm"
                                variant="outline"
                                aria-label={`${t('settings.parameters.remove', 'Entfernen')} ${labelOf(SYSTEM_LABELS, entry.system)}`}
                                onClick={() => setDraft((d) => d.filter((_, i) => i !== index))}
                            >
                                <Trash2 aria-hidden="true" size={14} />
                            </Button>
                        )}
                    </div>
                ))}
                {item.writable && (
                    <Button
                        size="sm"
                        variant="outline"
                        disabled={draft.length >= options.systems.length}
                        onClick={() =>
                            setDraft((d) => [
                                ...d,
                                { system: options.systems.find((s) => !used.includes(s)) ?? options.systems[0], registers: [] },
                            ])
                        }
                    >
                        <Plus aria-hidden="true" size={14} /> {t('settings.parameters.declared.add', 'System hinzufügen')}
                    </Button>
                )}
            </div>
        </EditorSection>
    );
}

/** A mapping `key → choice` where an empty choice leaves the key out (roles, precedence, matching). */
function MappingEditor({
    item,
    title,
    description,
    keys,
    keyLabel,
    choices,
    emptyLabel,
}: EditorProps & {
    title: string;
    description: string;
    keys: string[];
    keyLabel: (key: string) => string;
    choices: { value: string; label: string }[];
    emptyLabel: string;
}) {
    const { draft, setDraft, dirty } = useDraft<Record<string, string>>(item.value as Record<string, string>);
    const save = useSaveSetting(item);
    return (
        <EditorSection
            item={item}
            title={title}
            description={description}
            dirty={dirty}
            valid
            saving={save.isPending}
            onSave={() => save.mutate(draft)}
        >
            {keys.map((key) => (
                <SettingsRow key={key} label={keyLabel(key)} htmlFor={`${settingId(item)}-${key}`} field>
                    <NativeSelect
                        id={`${settingId(item)}-${key}`}
                        value={draft[key] ?? ''}
                        disabled={!item.writable}
                        onChange={(e) =>
                            setDraft((d) => {
                                const next = { ...d };
                                if (e.target.value) next[key] = e.target.value;
                                else delete next[key];
                                return next;
                            })
                        }
                    >
                        <option value="">{emptyLabel}</option>
                        {choices.map((choice) => (
                            <option key={choice.value} value={choice.value}>
                                {choice.label}
                            </option>
                        ))}
                    </NativeSelect>
                </SettingsRow>
            ))}
        </EditorSection>
    );
}

function RolesEditor(props: EditorProps) {
    const { t } = useTranslation();
    return (
        <MappingEditor
            {...props}
            title={t('settings.parameters.roles.title', 'Führende Quelle je Bereich')}
            description={t(
                'settings.parameters.roles.description',
                'Die führende Quelle eines Bereichs legt dessen Einträge an, alle anderen werden ihr zugeordnet. Ohne Eintrag führt das einzige System, das den Bereich liefert. Solange es zu einem Bereich schon Daten gibt, lässt sich die führende Quelle nicht wechseln.'
            )}
            keys={props.options.registers}
            keyLabel={(r) => labelOf(REGISTER_LABELS, r)}
            choices={props.options.systems.map((s) => ({ value: s, label: labelOf(SYSTEM_LABELS, s) }))}
            emptyLabel={t('settings.parameters.derived', 'Automatisch')}
        />
    );
}

function PrecedenceEditor(props: EditorProps) {
    const { t } = useTranslation();
    return (
        <MappingEditor
            {...props}
            title={t('settings.parameters.precedence.title', 'Wer bei widersprüchlichen Angaben gewinnt')}
            description={t(
                'settings.parameters.precedence.description',
                'Ohne Eintrag gewinnt die führende Quelle der Schläge. Wirkt ab dem nächsten Abgleich der Quelle.'
            )}
            keys={props.options.register_groups}
            keyLabel={(g) => labelOf(GROUP_LABELS, g)}
            choices={props.options.systems.map((s) => ({ value: s, label: labelOf(SYSTEM_LABELS, s) }))}
            emptyLabel={t('settings.parameters.standard', 'Standard')}
        />
    );
}

function MatchingEditor(props: EditorProps) {
    const { t } = useTranslation();
    return (
        <MappingEditor
            {...props}
            title={t('settings.parameters.matching.title', 'Zuordnung über Schlüssel')}
            description={t(
                'settings.parameters.matching.description',
                'Ob Zuordnungen über diesen Schlüssel sofort übernommen oder erst bestätigt werden. Wirkt beim nächsten Abgleich.'
            )}
            keys={props.options.match_keys}
            keyLabel={(k) => labelOf(MATCH_KEY_LABELS, k)}
            choices={[
                { value: 'auto', label: t('settings.parameters.matching.auto', 'Automatisch übernehmen') },
                { value: 'confirm', label: t('settings.parameters.matching.confirm', 'Erst bestätigen') },
            ]}
            emptyLabel={t('settings.parameters.matching.confirmDefault', 'Standard (bestätigen)')}
        />
    );
}

type Reconciliation = Record<string, { tolerance_pct?: number }>;

const RECONCILIATION_LABELS: Record<string, string> = {
    area_ha: 'Fläche (ha)',
    plant_count: 'Pflanzenzahl',
    variety: 'Sorte',
};

function ReconciliationEditor({ item }: EditorProps) {
    const { t } = useTranslation();
    const stored = item.value as Reconciliation;
    const [rows, setRows] = useState<{ attribute: string; tolerance: string }[]>([]);
    const loaded = JSON.stringify(stored);
    useEffect(() => {
        setRows(
            Object.entries(JSON.parse(loaded) as Reconciliation).map(([attribute, cfg]) => ({
                attribute,
                tolerance: cfg.tolerance_pct === undefined ? '' : String(cfg.tolerance_pct).replace('.', ','),
            }))
        );
    }, [loaded]);
    const value: Reconciliation | null = useMemo(() => {
        const out: Reconciliation = {};
        for (const row of rows) {
            const attribute = row.attribute.trim();
            if (!attribute || attribute in out) return null;
            const text = row.tolerance.trim().replace(',', '.');
            if (text === '') out[attribute] = {};
            else if (/^\d+(\.\d+)?$/.test(text)) out[attribute] = { tolerance_pct: Number(text) };
            else return null;
        }
        return out;
    }, [rows]);
    const save = useSaveSetting(item);
    const disabled = !item.writable;
    const attributeListId = useId();
    return (
        <EditorSection
            item={item}
            title={t('settings.parameters.reconciliation.title', 'Abgleich zwischen Quellen')}
            description={t(
                'settings.parameters.reconciliation.description',
                'Ab welcher Abweichung zwei Quellen als widersprüchlich gelten (in %). Ohne Toleranz wird nur auf Gleichheit geprüft. Wirkt beim nächsten Abgleich.'
            )}
            dirty={value !== null && JSON.stringify(value) !== loaded}
            valid={value !== null}
            saving={save.isPending}
            onSave={() => value && save.mutate(value)}
        >
            <div className="farm-parameters__rows">
                {rows.map((row, index) => (
                    <div className="farm-parameters__share" key={index}>
                        <Input
                            aria-label={`${t('settings.parameters.reconciliation.attribute', 'Merkmal')} ${index + 1}`}
                            title={RECONCILIATION_LABELS[row.attribute]}
                            list={attributeListId}
                            value={row.attribute}
                            disabled={disabled}
                            onChange={(e) => setRows((r) => r.map((x, i) => (i === index ? { ...x, attribute: e.target.value } : x)))}
                        />
                        <div className="farm-parameters__percent">
                            <Input
                                aria-label={`${t('settings.parameters.reconciliation.tolerance', 'Toleranz in %')} ${index + 1}`}
                                inputMode="decimal"
                                value={row.tolerance}
                                disabled={disabled}
                                onChange={(e) => setRows((r) => r.map((x, i) => (i === index ? { ...x, tolerance: e.target.value } : x)))}
                            />
                            <span aria-hidden="true">%</span>
                        </div>
                        {!disabled && (
                            <Button
                                size="sm"
                                variant="outline"
                                aria-label={`${t('settings.parameters.remove', 'Entfernen')} ${row.attribute || index + 1}`}
                                onClick={() => setRows((r) => r.filter((_, i) => i !== index))}
                            >
                                <Trash2 aria-hidden="true" size={14} />
                            </Button>
                        )}
                    </div>
                ))}
                <datalist id={attributeListId}>
                    {Object.keys(RECONCILIATION_LABELS).map((attribute) => (
                        <option key={attribute} value={attribute}>
                            {RECONCILIATION_LABELS[attribute]}
                        </option>
                    ))}
                </datalist>
                {!disabled && (
                    <Button size="sm" variant="outline" onClick={() => setRows((r) => [...r, { attribute: '', tolerance: '' }])}>
                        <Plus aria-hidden="true" size={14} /> {t('settings.parameters.reconciliation.add', 'Merkmal hinzufügen')}
                    </Button>
                )}
                {value === null && (
                    <p className="farm-parameters__problem" role="alert">
                        {t(
                            'settings.parameters.reconciliation.invalid',
                            'Jedes Merkmal einmal, die Toleranz als Zahl ab 0 oder leer.'
                        )}
                    </p>
                )}
            </div>
        </EditorSection>
    );
}

/* -------------------------------------------------------------------------------- the country */

function CountryEditor({ item, options }: EditorProps) {
    const { t } = useTranslation();
    const { draft, setDraft, dirty } = useDraft<string>(item.value as string);
    const save = useSaveSetting(item);
    const names = useMemo(() => {
        try {
            return new Intl.DisplayNames(['de'], { type: 'region' });
        } catch {
            return null;
        }
    }, []);
    return (
        <EditorSection
            item={item}
            title={t('settings.parameters.country.title', 'Land des Betriebs')}
            description={t(
                'settings.parameters.country.description',
                'Bestimmt die Ländervorwahl für Telefonnummern ohne Vorwahl. Beim nächsten Abgleich werden alle Nummern damit neu formatiert.'
            )}
            dirty={dirty}
            valid
            saving={save.isPending}
            onSave={() => save.mutate(draft)}
        >
            <SettingsRow label={t('settings.parameters.country.label', 'Land')} htmlFor="farm-country" field>
                <NativeSelect id="farm-country" value={draft} disabled={!item.writable} onChange={(e) => setDraft(e.target.value)}>
                    {options.countries.map((code) => (
                        <option key={code} value={code}>
                            {names?.of(code) ?? code}
                        </option>
                    ))}
                </NativeSelect>
            </SettingsRow>
        </EditorSection>
    );
}

/** `namespace.key` → its editor. Every entry of `FARM_PARAMETER_SETTINGS` has one (pinned by test). */
export const EDITORS: Record<string, ComponentType<EditorProps>> = {
    'labour_cost.fixed_shares': FixedSharesEditor,
    'labour.catalogue': CatalogueEditor,
    'labour.access': AccessEditor,
    'sources.declared': DeclaredEditor,
    'sources.roles': RolesEditor,
    'sources.precedence': PrecedenceEditor,
    'sources.reconciliation': ReconciliationEditor,
    'sources.matching': MatchingEditor,
    'farm.country': CountryEditor,
};
